<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { getUnixStartOfDay, getUnixEndOfDay } from 'helpers/DateHelper';
import subDays from 'date-fns/subDays';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import ContactAPI from 'dashboard/api/contacts';
import { downloadBinaryFile } from 'dashboard/helper/downloadHelper';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ContactLabels from 'dashboard/components-next/Contacts/ContactLabels/ContactLabels.vue';
import ContactsForm from 'dashboard/components-next/Contacts/ContactsForm/ContactsForm.vue';
import ConfirmContactDeleteDialog from 'dashboard/components-next/Contacts/ContactsForm/ConfirmContactDeleteDialog.vue';
import Policy from 'dashboard/components/policy.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';

const props = defineProps({
  selectedContact: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['goToContactsList']);

const { t } = useI18n();
const store = useStore();

const confirmDeleteContactDialogRef = ref(null);

const avatarFile = ref(null);
const avatarUrl = ref('');

const contactsFormRef = ref(null);

const uiFlags = useMapGetter('contacts/getUIFlags');
const isUpdating = computed(() => uiFlags.value.isUpdating);

const isFormInvalid = computed(() => contactsFormRef.value?.isFormInvalid);

const contactData = ref({});
const isExportingConversations = ref(false);
const customDateRange = ref([subDays(new Date(), 6), new Date()]);
const selectedDateRange = ref(DATE_RANGE_TYPES.LAST_7_DAYS);
const delimiter = ref('semicolon');
const includeAttachments = ref(false);

const delimiterOptions = computed(() => [
  {
    value: 'semicolon',
    label: t(
      'CONTACTS_LAYOUT.DETAILS.EXPORT_CONVERSATIONS.DELIMITERS.SEMICOLON'
    ),
  },
  {
    value: 'comma',
    label: t('CONTACTS_LAYOUT.DETAILS.EXPORT_CONVERSATIONS.DELIMITERS.COMMA'),
  },
  {
    value: 'tab',
    label: t('CONTACTS_LAYOUT.DETAILS.EXPORT_CONVERSATIONS.DELIMITERS.TAB'),
  },
  {
    value: 'pipe',
    label: t('CONTACTS_LAYOUT.DETAILS.EXPORT_CONVERSATIONS.DELIMITERS.PIPE'),
  },
]);

const from = computed(() => getUnixStartOfDay(customDateRange.value[0]));
const to = computed(() => getUnixEndOfDay(customDateRange.value[1]));

const getInitialContactData = () => {
  if (!props.selectedContact) return {};
  return { ...props.selectedContact };
};

onMounted(() => {
  Object.assign(contactData.value, getInitialContactData());
});

const createdAt = computed(() => {
  return contactData.value?.createdAt
    ? dynamicTime(contactData.value.createdAt)
    : '';
});

const lastActivityAt = computed(() => {
  return contactData.value?.lastActivityAt
    ? dynamicTime(contactData.value.lastActivityAt)
    : '';
});

const avatarSrc = computed(() => {
  return avatarUrl.value ? avatarUrl.value : contactData.value?.thumbnail;
});

const handleFormUpdate = updatedData => {
  Object.assign(contactData.value, updatedData);
};

const updateContact = async () => {
  try {
    const {
      id,
      name,
      email,
      phoneNumber,
      additionalAttributes,
      customAttributes,
    } = contactData.value;
    await store.dispatch('contacts/update', {
      id,
      name,
      email,
      phoneNumber,
      additionalAttributes,
      customAttributes,
    });
    await store.dispatch(
      'contacts/fetchContactableInbox',
      props.selectedContact.id
    );
    useAlert(t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.ERROR_MESSAGE'));
  }
};

const onDateRangeChange = value => {
  const [startDate, endDate, rangeType] = value;
  customDateRange.value = [startDate, endDate];
  selectedDateRange.value = rangeType || DATE_RANGE_TYPES.CUSTOM_RANGE;
};

const downloadConversations = async () => {
  if (!props.selectedContact?.id) return;

  isExportingConversations.value = true;
  try {
    const response = await ContactAPI.exportContactConversations(
      props.selectedContact.id,
      {
        since: from.value,
        until: to.value,
        delimiter: delimiter.value,
        include_attachments: includeAttachments.value,
      }
    );

    const extension = includeAttachments.value ? 'zip' : 'csv';
    const fileName = `contact-${props.selectedContact.id}-conversations.${extension}`;
    const contentType = includeAttachments.value
      ? 'application/zip'
      : 'text/csv;charset=utf-8';

    downloadBinaryFile(fileName, response.data, contentType);
  } catch (error) {
    useAlert(t('CONTACTS_LAYOUT.DETAILS.EXPORT_CONVERSATIONS.ERROR_MESSAGE'));
  } finally {
    isExportingConversations.value = false;
  }
};

const openConfirmDeleteContactDialog = () => {
  confirmDeleteContactDialogRef.value?.dialogRef.open();
};

const handleAvatarUpload = async ({ file, url }) => {
  avatarFile.value = file;
  avatarUrl.value = url;

  try {
    await store.dispatch('contacts/update', {
      ...contactsFormRef.value?.state,
      avatar: file,
      isFormData: true,
    });
    useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.UPLOAD.SUCCESS_MESSAGE'));
  } catch {
    useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.UPLOAD.ERROR_MESSAGE'));
  }
};

const handleAvatarDelete = async () => {
  try {
    if (props.selectedContact && props.selectedContact.id) {
      await store.dispatch('contacts/deleteAvatar', props.selectedContact.id);
      useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.DELETE.SUCCESS_MESSAGE'));
    }
    avatarFile.value = null;
    avatarUrl.value = '';
    contactData.value.thumbnail = null;
  } catch (error) {
    useAlert(
      error.message
        ? error.message
        : t('CONTACTS_LAYOUT.DETAILS.AVATAR.DELETE.ERROR_MESSAGE')
    );
  }
};
</script>

<template>
  <div class="flex flex-col items-start gap-8 pb-6">
    <div class="flex flex-col items-start gap-3">
      <Avatar
        :src="avatarSrc || ''"
        :name="selectedContact?.name || ''"
        :size="72"
        allow-upload
        @upload="handleAvatarUpload"
        @delete="handleAvatarDelete"
      />
      <div class="flex flex-col gap-1">
        <h3 class="text-base font-medium text-n-slate-12">
          {{ selectedContact?.name }}
        </h3>
        <div class="flex flex-col gap-1.5">
          <span
            v-if="selectedContact?.identifier"
            class="inline-flex items-center gap-1 text-sm text-n-slate-11"
          >
            <span class="i-ph-user-gear text-n-slate-10 size-4" />
            {{ selectedContact?.identifier }}
          </span>
          <span class="inline-flex items-center gap-1 text-sm text-n-slate-11">
            <span
              v-if="selectedContact?.identifier"
              class="i-ph-activity text-n-slate-10 size-4"
            />
            {{ $t('CONTACTS_LAYOUT.DETAILS.CREATED_AT', { date: createdAt }) }}
            •
            {{
              $t('CONTACTS_LAYOUT.DETAILS.LAST_ACTIVITY', {
                date: lastActivityAt,
              })
            }}
          </span>
        </div>
      </div>
      <ContactLabels :contact-id="selectedContact?.id" />
    </div>
    <div class="flex flex-col items-start gap-6">
      <ContactsForm
        ref="contactsFormRef"
        :contact-data="contactData"
        is-details-view
        @update="handleFormUpdate"
      />
      <Button
        :label="t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.UPDATE_BUTTON')"
        size="sm"
        :is-loading="isUpdating"
        :disabled="isUpdating || isFormInvalid"
        @click="updateContact"
      />
    </div>
    <Policy :permissions="['administrator']">
      <div
        class="flex w-full flex-col gap-5 rounded-xl border border-n-weak bg-n-surface-2 p-5"
      >
        <div class="flex items-start gap-3">
          <span
            class="i-lucide-file-down mt-0.5 size-5 shrink-0 text-n-brand"
          />
          <div class="flex flex-col gap-1">
            <h6 class="text-base font-medium text-n-slate-12">
              {{ t('CONTACTS_LAYOUT.DETAILS.EXPORT_CONVERSATIONS.TITLE') }}
            </h6>
            <span class="text-sm text-n-slate-11">
              {{
                t('CONTACTS_LAYOUT.DETAILS.EXPORT_CONVERSATIONS.DESCRIPTION')
              }}
            </span>
          </div>
        </div>
        <div class="flex w-full flex-col gap-4">
          <div class="grid w-full gap-4 lg:grid-cols-2">
            <div class="flex flex-col gap-1.5">
              <label class="text-sm font-medium text-n-slate-12">
                {{
                  t('CONTACTS_LAYOUT.DETAILS.EXPORT_CONVERSATIONS.PERIOD_LABEL')
                }}
              </label>
              <WootDatePicker
                v-model:date-range="customDateRange"
                v-model:range-type="selectedDateRange"
                @date-range-changed="onDateRangeChange"
              />
            </div>
            <div class="flex flex-col gap-1.5">
              <label class="text-sm font-medium text-n-slate-12">
                {{
                  t(
                    'CONTACTS_LAYOUT.DETAILS.EXPORT_CONVERSATIONS.DELIMITER_LABEL'
                  )
                }}
              </label>
              <Select
                v-model="delimiter"
                :options="delimiterOptions"
                :disabled="isExportingConversations"
                class="!w-full [&_select]:!w-full"
              />
              <span class="text-xs text-n-slate-10">
                {{
                  t(
                    'CONTACTS_LAYOUT.DETAILS.EXPORT_CONVERSATIONS.DELIMITER_HELP'
                  )
                }}
              </span>
            </div>
          </div>
          <label
            class="flex cursor-pointer items-start gap-3 rounded-lg bg-n-alpha-2 p-3"
          >
            <Checkbox
              v-model="includeAttachments"
              :disabled="isExportingConversations"
              class="mt-0.5 shrink-0"
            />
            <span class="flex flex-col gap-0.5">
              <span class="text-sm font-medium text-n-slate-12">
                {{
                  t(
                    'CONTACTS_LAYOUT.DETAILS.EXPORT_CONVERSATIONS.INCLUDE_ATTACHMENTS'
                  )
                }}
              </span>
              <span class="text-xs text-n-slate-10">
                {{
                  t(
                    'CONTACTS_LAYOUT.DETAILS.EXPORT_CONVERSATIONS.ATTACHMENTS_HELP'
                  )
                }}
              </span>
            </span>
          </label>
          <Button
            :label="t('CONTACTS_LAYOUT.DETAILS.EXPORT_CONVERSATIONS.DOWNLOAD')"
            size="sm"
            :is-loading="isExportingConversations"
            :disabled="isExportingConversations"
            icon="i-lucide-download"
            class="self-end"
            @click="downloadConversations"
          />
        </div>
      </div>
      <div
        class="flex flex-col items-start w-full gap-4 pt-6 border-t border-n-strong"
      >
        <div class="flex flex-col gap-2">
          <h6 class="text-base font-medium text-n-slate-12">
            {{ t('CONTACTS_LAYOUT.DETAILS.DELETE_CONTACT') }}
          </h6>
          <span class="text-sm text-n-slate-11">
            {{ t('CONTACTS_LAYOUT.DETAILS.DELETE_CONTACT_DESCRIPTION') }}
          </span>
        </div>
        <Button
          :label="t('CONTACTS_LAYOUT.DETAILS.DELETE_CONTACT')"
          color="ruby"
          @click="openConfirmDeleteContactDialog"
        />
      </div>
      <ConfirmContactDeleteDialog
        ref="confirmDeleteContactDialogRef"
        :selected-contact="selectedContact"
        @go-to-contacts-list="emit('goToContactsList')"
      />
    </Policy>
  </div>
</template>
